import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    InputAdornment,
    MenuItem,
    Pagination,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useTranslation} from "react-i18next";
import {useJobPositions} from "../../../hooks/useJobPositions";

import AssignJobToTenantDialog from "./dialogs/AssignJobToTenantDialog";
import EditJobPositionDialog from "./dialogs/EditJobPositionDialog";
import AddJobPositionDialog from "./dialogs/AddJobPositionDialog";
import CreateJobPositionDialog from "./dialogs/CreateJobPositionDialog";
import JobPositionsTable from "./tables/JobPositionsTable";
import JobPositionsMobile from "./mbile/JobPositionsMobile";
import JobPositionDetails from "./JobPositionDetails";

function StatCard({icon, value, label}: Readonly<{ icon: React.ReactNode; value: number; label: string }>) {
    return (
        <Card variant="outlined" sx={{overflow: "hidden"}}>
            <CardContent sx={{
                display: "flex",
                alignItems: "center",
                gap: {xs: 1, sm: 2},
                p: {xs: 1, sm: 2.5},
                "&:last-child": {pb: {xs: 1, sm: 2.5}},
            }}>
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    width: {xs: 32, sm: 56}, height: {xs: 32, sm: 56}, borderRadius: "50%",
                    bgcolor: (theme) => `${theme.palette.primary.main}1F`, color: "primary.main",
                }}>
                    {icon}
                </Box>
                <Box sx={{minWidth: 0, overflow: "hidden"}}>
                    <Typography variant="h5" fontWeight={700} lineHeight={1.1}>{value}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function JobPositions(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {
        error,
        setError,
        branches,

        // assignments (branch-scoped rows) + their 3-dot menu
        jobs,
        loading,
        anchorEl,
        menuOpen,
        menuRowId,
        handleMenuOpen,
        handleMenuClose,
        handleMenuAction,
        isParentRejected,
        handleMainApprove,
        handleMainReject,
        handleMainClose,

        // edit dialog
        editDialogOpen,
        editName,
        setEditName,
        editActive,
        setEditActive,
        editFormErrors,
        setEditFormErrors,
        handleEditOpen,
        handleEditClose,
        handleEditSave,

        // assign-edit dialog
        assignEditOpen,
        assignEditJobId,
        setAssignEditJobId,
        assignEditActive,
        setAssignEditActive,
        approvedJobOptions,
        approvedJobsLoading,
        handleAssignEditOpen,
        handleAssignEditClose,
        handleAssignEditSave,

        // assign-to-branch dialog
        addDialogOpen,
        addJobId,
        setAddJobId,
        addActive,
        setAddActive,
        handleAddOpen,
        handleAddClose,
        handleAddSave,

        // create position dialog
        inlineAddName,
        setInlineAddName,
        inlineAddActive,
        setInlineAddActive,
        inlineAddSaving,
        handleInlineAdd,

        // positions list
        allJobs,
        allJobsLoading,
        statusFilter,
        setStatusFilter,
        cardSearch,
        setCardSearch,
        setCardSearchQuery,
        cardOrder,
        cardOrderBy,
        handleCardSort,
        cardSortedJobs,
        cardPaginatedJobs,
        cardPage,
        setCardPage,
        itemsPerPage,
        setSelectedPositionId,
        selectedPosition,
        selectedPositionAssignments,
        cardAnchorEl,
        cardMenuOpen,
        cardMenuRowId,
        handleCardMenuOpen,
        handleCardMenuClose,
        handleCardMenuAction,
        handleApprove,
        handleReject,
        handleClose,

        // snackbar
        snackbarOpen,
        setSnackbarOpen,
        snackbarText,
        snackbarSeverity,
    } = useJobPositions();

    const [createOpen, setCreateOpen] = React.useState<boolean>(false);

    const branchCounts = React.useMemo(() => {
        const counts: Record<string, number> = {};
        jobs.forEach((j) => {
            counts[j.name] = (counts[j.name] ?? 0) + 1;
        });
        return counts;
    }, [jobs]);

    const handleCreateSave = async () => {
        if (await handleInlineAdd()) setCreateOpen(false);
    };

    const activePositions = allJobs.filter((j) => j.active).length;
    const total = cardSortedJobs.length;
    const from = total === 0 ? 0 : (cardPage - 1) * itemsPerPage + 1;
    const to = Math.min(cardPage * itemsPerPage, total);

    const assignmentProps = {
        loading,
        isParentRejected,
        anchorEl,
        menuOpen,
        menuRowId,
        handleMenuOpen,
        handleMenuClose,
        handleMenuAction,
        handleAssignEditOpen,
        handleMainApprove,
        handleMainReject,
        handleMainClose,
    };

    const searchField = (
        <TextField
            placeholder={t("hr.jobs.listing.searchPlaceholder")}
            size="small"
            fullWidth
            value={cardSearch}
            onChange={(e) => {
                setCardSearch(e.target.value);
                setCardSearchQuery(e.target.value);
            }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start"><SearchIcon color="action"/></InputAdornment>
                    ),
                    endAdornment: cardSearch ? (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => {
                                setCardSearch("");
                                setCardSearchQuery("");
                            }}>
                                <ClearIcon fontSize="small"/>
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                },
            }}
        />
    );

    const statusSelect = (
        <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as "Active" | "Inactive" | "All")}
            sx={{minWidth: 130}}
        >
            <MenuItem value="Active">{t("common.status.active")}</MenuItem>
            <MenuItem value="Inactive">{t("common.status.inactive")}</MenuItem>
            <MenuItem value="All">{t("common.filters.all")}</MenuItem>
        </Select>
    );

    return (
        <Box sx={{width: "100%", textAlign: "left", display: "flex", flexDirection: "column", gap: 2}}>
            {/* Page header: the page title lives here only (JobPage renders no breadcrumb), left-aligned. */}
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2}}>
                <Box sx={{minWidth: 0, textAlign: "left"}}>
                    <Typography variant="h5" component="h1" fontWeight={700}
                                sx={{textAlign: "left"}}>
                        {t("hr.jobs.page.title")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{textAlign: "left"}}>
                        {t("hr.jobs.page.subtitle")}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setCreateOpen(true)}
                        sx={{flexShrink: 0, whiteSpace: "nowrap"}}>
                    {isMobile ? t("hr.jobs.page.addShort") : t("hr.jobs.page.add")}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
            )}

            <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: {xs: 1, sm: 2}}}>
                <StatCard icon={<WorkOutlineIcon/>} value={activePositions} label={t("hr.jobs.stats.activePositions")}/>
                <StatCard icon={<AccountTreeOutlinedIcon/>} value={branches.length} label={t("hr.jobs.stats.branches")}/>
                <StatCard icon={<LinkOutlinedIcon/>} value={jobs.length} label={t("hr.jobs.stats.assignments")}/>
            </Box>

            {isMobile ? (
                <>
                    <Box sx={{display: "flex", gap: 1}}>
                        <Box sx={{flex: 1, minWidth: 0}}>{searchField}</Box>
                        {statusSelect}
                    </Box>
                    <JobPositionsMobile
                        loading={allJobsLoading}
                        positions={cardSortedJobs}
                        branches={branches}
                        assignments={jobs}
                        assignmentLoading={loading}
                        assignmentProps={assignmentProps}
                        onEdit={handleEditOpen}
                        onAssign={(branchId) => handleAddOpen(branchId)}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onClose={handleClose}
                    />
                </>
            ) : (
                <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 8}}>
                        <Card variant="outlined" sx={{border: "1px solid", borderColor: "divider", height: "100%"}}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} sx={{mb: 2}}>
                                    {t("hr.jobs.list.title")}
                                </Typography>
                                <Stack direction="row" spacing={1.5} sx={{mb: 2}}>
                                    {searchField}
                                    {statusSelect}
                                </Stack>
                                <JobPositionsTable
                                    loading={allJobsLoading}
                                    positions={cardPaginatedJobs}
                                    selectedId={selectedPosition?.id ?? null}
                                    onSelect={setSelectedPositionId}
                                    branchCounts={branchCounts}
                                    order={cardOrder}
                                    orderBy={cardOrderBy}
                                    onSort={handleCardSort}
                                    anchorEl={cardAnchorEl}
                                    menuOpen={cardMenuOpen}
                                    menuRowId={cardMenuRowId}
                                    handleMenuOpen={handleCardMenuOpen}
                                    handleMenuClose={handleCardMenuClose}
                                    handleMenuAction={handleCardMenuAction}
                                    handleEditOpen={handleEditOpen}
                                    handleApprove={handleApprove}
                                    handleReject={handleReject}
                                    handleClose={handleClose}
                                />
                                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2}}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("hr.jobs.list.range", {from, to, total})}
                                    </Typography>
                                    <Pagination
                                        count={Math.max(1, Math.ceil(total / itemsPerPage))}
                                        color="primary"
                                        shape="rounded"
                                        page={cardPage}
                                        onChange={(_e, page) => setCardPage(page)}
                                        disabled={allJobsLoading || total === 0}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Stack spacing={2}>
                            <Card variant="outlined" sx={{border: "1px solid", borderColor: "divider"}}>
                                <CardContent>
                                    {selectedPosition ? (
                                        <JobPositionDetails
                                            showHeader
                                            section="details"
                                            position={selectedPosition}
                                            branches={branches}
                                            assignmentProps={{...assignmentProps, assignments: selectedPositionAssignments}}
                                            onEdit={handleEditOpen}
                                            onAssign={(branchId) => handleAddOpen(branchId)}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {t("hr.jobs.details.empty")}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                            {selectedPosition && (
                                <Card variant="outlined" sx={{border: "1px solid", borderColor: "divider"}}>
                                    <CardContent>
                                        <JobPositionDetails
                                            section="assignments"
                                            position={selectedPosition}
                                            branches={branches}
                                            assignmentProps={{...assignmentProps, assignments: selectedPositionAssignments}}
                                            onEdit={handleEditOpen}
                                            onAssign={(branchId) => handleAddOpen(branchId)}
                                        />
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            )}

            <Card variant="outlined" sx={{border: "1px solid", borderColor: "divider"}}>
                <CardContent sx={{display: "flex", alignItems: "center", gap: 2, "&:last-child": {pb: 2}}}>
                    <Box sx={{
                        display: "flex", p: 1, borderRadius: "50%", flexShrink: 0,
                        bgcolor: (theme) => `${theme.palette.info.main}1F`, color: "info.main",
                    }}>
                        <InfoOutlinedIcon/>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{t("hr.jobs.page.info")}</Typography>
                </CardContent>
            </Card>

            <CreateJobPositionDialog
                open={createOpen}
                name={inlineAddName}
                setName={setInlineAddName}
                active={inlineAddActive}
                setActive={setInlineAddActive}
                saving={inlineAddSaving}
                onClose={() => setCreateOpen(false)}
                onSave={handleCreateSave}
            />

            <AssignJobToTenantDialog
                open={assignEditOpen}
                assignEditJobId={assignEditJobId}
                setAssignEditJobId={setAssignEditJobId}
                assignEditActive={assignEditActive}
                setAssignEditActive={setAssignEditActive}
                approvedJobOptions={approvedJobOptions}
                approvedJobsLoading={approvedJobsLoading}
                onClose={handleAssignEditClose}
                onSave={handleAssignEditSave}
            />

            <EditJobPositionDialog
                open={editDialogOpen}
                editName={editName}
                setEditName={setEditName}
                editActive={editActive}
                setEditActive={setEditActive}
                editFormErrors={editFormErrors}
                setEditFormErrors={setEditFormErrors}
                onClose={handleEditClose}
                onSave={handleEditSave}
            />

            <AddJobPositionDialog
                open={addDialogOpen}
                addJobId={addJobId}
                setAddJobId={setAddJobId}
                addActive={addActive}
                setAddActive={setAddActive}
                approvedJobOptions={approvedJobOptions}
                approvedJobsLoading={approvedJobsLoading}
                onClose={handleAddClose}
                onSave={handleAddSave}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
            >
                <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} sx={{width: "100%"}}>
                    {snackbarText}
                </Alert>
            </Snackbar>
        </Box>
    );
}
